/**
 * This component uses Portable Text to render a post body.
 *
 * You can learn more about Portable Text on:
 * https://www.sanity.io/docs/block-content
 * https://github.com/portabletext/react-portabletext
 * https://portabletext.org/
 *
 */

import { Link } from "@/components/Link";
import {
  PortableText,
  type PortableTextComponents,
  type PortableTextBlock,
} from "next-sanity";
import { createElement } from "react";

export default function CustomPortableText({
  className,
  value,
  bodyLargeClassName = 'body--large',
  useSmallBody = false,
  outlineEyebrows = false,
  promoteFirstHeading = false,
  promoteH3HeadingsToH2 = false,
}: {
  className?: string;
  value: PortableTextBlock[];
  bodyLargeClassName?: 'body' | 'body--large';
  useSmallBody?: boolean;
  /** Draw the oval outline (in --color-accent) around eyebrow text. */
  outlineEyebrows?: boolean;
  /** Promote only the first Portable Text heading while preserving its visual style. */
  promoteFirstHeading?: boolean;
  /** Promote level-three section headings while preserving their visual style. */
  promoteH3HeadingsToH2?: boolean;
}) {
  const firstHeading = promoteFirstHeading
    ? value.find((block) => /^h[1-6]$/.test(String(block.style)))
    : undefined;
  const firstHeadingStyle = firstHeading?.style as `h${1 | 2 | 3 | 4 | 5 | 6}` | undefined;

  const components: PortableTextComponents = {
    block: {
      bodyLrg:  ({ children }) => <p className={bodyLargeClassName}>{children}</p>,
      bodySm:  ({ children }) => <p className="body--small">{children}</p>,
      eyebrow: ({ children }) => (
        <p className="eyebrow">
          {outlineEyebrows ? (
            <span className="is-outline relative whitespace-nowrap bg-transparent before:content-[''] before:-z-1 before:absolute before:border-4 before:border-(--color-accent) before:block before:rounded-[50%] before:inset-[-10px_-20px]">
              {children}
            </span>
          ) : (
            children
          )}
        </p>
      ),
      ...(promoteH3HeadingsToH2 ? {
        h3: ({ children }) => <h2 className="h3">{children}</h2>,
      } : {}),
      ...(firstHeadingStyle && firstHeading?._key ? {
        [firstHeadingStyle]: ({ children, value: block }) => (
          block._key === firstHeading._key
            ? <h1 className={firstHeadingStyle}>{children}</h1>
            : createElement(firstHeadingStyle, null, children)
        ),
      } : {}),
      ...(useSmallBody ? { normal: ({ children }) => <p className="body--small">{children}</p> } : {}),
    },
    ...(useSmallBody ? {
      list: {
        bullet: ({ children }) => <ul className="body--small list-disc list-outside pl-5">{children}</ul>,
      },
      listItem: {
        bullet: ({ children }) => <li className="pl-1">{children}</li>,
      },
    } : {}),
    marks: {
      link: ({ children, value }) => {
        return (
          <Link link={value}>{children}</Link>
        )
      },

      mark: ({ text }) => {
        const textArr = text.split(' ');

        // No text-box-trim on the mark: Safari applies it to inline elements
        // (Chrome ignores it there), shrinking the box the highlight is sized
        // from — the highlight rendered ~30% shorter in Safari.
        return textArr.map((part, i) => (
          <mark key={i} className="relative bg-transparent before:content-[''] before:-z-1 before:absolute before:bg-(--color-accent) before:block before:rounded before:inset-x-[-0.35em] before:-top-[0.05em] before:bottom-[0.12em]">{part}{i === textArr.length - 1 ? '' : ' '}</mark>
        ));
      },

      outline: ({ children }) => {
        return (
          <span className="is-outline relative whitespace-nowrap bg-transparent before:content-[''] before:-z-1 before:absolute before:border-[0.18em] before:border-(--color-accent) before:block before:rounded-[50%] before:inset-[-.15em_-.35em]">{children}</span>
        )
      },
    },
  };

  return (
    <div className={["prose relative z-0", className].filter(Boolean).join(" ")}>
      <PortableText components={components} value={value} />
    </div>
  );
}
